import type { AfterViewInit, OnInit } from '@angular/core'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  viewChild
} from '@angular/core'
import type { FileService } from '@awork/_shared/services/file-service/file.service'
import { Subject, distinctUntilChanged, fromEvent, merge, throttleTime } from 'rxjs'
import { OverlayComponent } from '../../layout/overlay/overlay.component'
import { EntityFileDropComponent } from '../entity-file-drop/entity-file-drop.component'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import type { ValidFileEntities, ValidFileEntityTypes } from '@awork/_shared/services/file-service/types'

@Component({
  selector: 'aw-file-drop-overlay',
  templateUrl: './file-drop-overlay.component.html',
  styleUrls: ['./file-drop-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [EntityFileDropComponent, OverlayComponent]
})
export class FileDropOverlayComponent implements OnInit, AfterViewInit {
  element = inject(ElementRef)
  private cdr = inject(ChangeDetectorRef)

  readonly entity = input<ValidFileEntities>()
  readonly entityType = input<ValidFileEntityTypes>()

  readonly hiding = output<void>()

  readonly overlay = viewChild<OverlayComponent>('overlay')

  dragOver$: Subject<boolean> = new Subject<boolean>()
  private destroyRef = inject(DestroyRef)

  fileService: FileService
  classes = ''

  ngOnInit(): void {
    this.dragOver$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        throttleTime(50, undefined, { leading: true, trailing: true })
      )
      .subscribe(isDraggingOver => {
        if (!isDraggingOver) {
          this.hide()
        }
      })
  }

  /**
   * Shows the overlay and observes drag completion outside its drop target.
   */
  ngAfterViewInit(): void {
    // Capture drops before other targets, such as the composer, stop propagation.
    merge(fromEvent(document, 'drop', { capture: true }), fromEvent(document, 'dragend', { capture: true }))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        // Let our own drop target upload before it hides and destroys the overlay.
        if (event.type === 'drop' && event.composedPath().includes(this.element.nativeElement)) {
          return
        }

        this.hide()
      })

    const showTimer = setTimeout(() => {
      const overlay = this.overlay()

      if (overlay && overlay.show) {
        this.show()
      }
    })

    this.destroyRef.onDestroy(() => clearTimeout(showTimer))
  }

  /**
   * Shows the overlay
   */
  show(): void {
    this.overlay().show()
    this.cdr.markForCheck()
  }

  /**
   * Hides the overlay
   */
  hide(): void {
    this.overlay().hide()
    this.cdr.markForCheck()
  }

  /**
   * Callback triggered when the file is leaving the overlay
   * @param {DragEvent} event
   */
  dragLeave(event: DragEvent): void {
    event.stopPropagation()
    event.preventDefault()

    setTimeout(() => {
      this.dragOver$.next(false)
    }, 200)
  }

  /**
   * Callback triggered when the file is dragged over the overlay
   * @param {DragEvent} event
   */
  dragOver(event: DragEvent): void {
    event.stopPropagation()
    event.preventDefault()

    if (this.isDragOverImageEmbed(event)) {
      this.hide()

      return
    }

    this.dragOver$.next(true)
  }

  /**
   * Checks whether the current drag pointer is over an image-embed upload node.
   * @param {DragEvent} event
   * @returns {boolean}
   */
  private isDragOverImageEmbed(event: DragEvent): boolean {
    const imageEmbedSelector = 'aw-rich-text-image-upload, .rich-text-image-upload__container, span.image-upload'
    const eventTarget = event.target

    if (eventTarget instanceof Element) {
      const isImageEmbedTarget = eventTarget.matches(imageEmbedSelector)
      const hasImageEmbedParent = Boolean(eventTarget.closest(imageEmbedSelector))

      if (isImageEmbedTarget || hasImageEmbedParent) {
        return true
      }
    }

    if (typeof document.elementsFromPoint !== 'function') {
      return false
    }

    return document.elementsFromPoint(event.clientX, event.clientY).some(element => element.matches(imageEmbedSelector))
  }
}
